import {injectable} from "inversify";
import {
    InAppPurchase2,
    IAPProduct,
    PlayStoreReceipt,
    AppStoreReceipt, IAPProductOptions, IAPError
} from '@awesome-cordova-plugins/in-app-purchase-2/ngx';
import ShopStore from "@/store/shop";
import {BankOption} from "@/store/shop/types";
import http from "@/axios";
import PlayerStore from "@/store/player";
import {TenjinService} from "@/services/tenjin-service";

@injectable()
export class IapService {

    private tenjinService!: TenjinService;
    private isInitialized: boolean = false;

    public products: IAPProduct[] = []
    // @ts-ignore
    private purchaseStore = typeof window.store !== 'undefined' ? window.store : undefined;

    public static IapEnabled(): boolean {
        return process.env.VUE_APP_IS_MOBILE_APP === 'true' &&
            !(process.env.VUE_APP_MOBILE_APP_TYPE === 'RU' || process.env.VUE_APP_MOBILE_APP_TYPE === 'RU_DEV')
    }

    public static logClient(message: string): Promise<void> {
        const params = new URLSearchParams();
        params.append('message', message);
        params.append('domain', `Mobile app ${process.env.VUE_APP_MOBILE_APP_TYPE} v${process.env.VUE_APP_ANDROID_VERSION_CODE}`);
        return http.post('/api/log/add', params);
    }

    public static log(message: string){
        console.log(message)
        IapService.logClient(message)
    }

    public async initialize(tenjinService: TenjinService) {
        if (this.isInitialized) {
            return;
        }

        IapService.log("PurchaseStore: iapService initialize")

        this.tenjinService = tenjinService;

        if (process.env.VUE_APP_CAS_DEBUG === 'true') {
            this.purchaseStore.verbosity = this.purchaseStore.DEBUG
        }
        this.registerProducts()

        // Получаем информацию о продукте из стора
        IapService.log(`PurchaseStore: ready`)
        this.purchaseStore.ready(() => {
            this.products = this.purchaseStore.products
        })
        this.isInitialized = true;
    }

    registerProducts() {

        var bankProductsIds: string[] = []

        ShopStore.fetchBankInfo().then(() => {
            const bankOptions: BankOption[] = ShopStore.getBankOptions()
            let bankProducts: IAPProductOptions[] = []

            bankOptions.forEach((bo, i) => {
                var bankId: string = "gems_" + (bo.gems / 10)
                bankProductsIds.push(bankId)
                bankProducts.push({
                    id: bankId,
                    type: this.purchaseStore.CONSUMABLE
                })
            })

            IapService.log(`PurchaseStore: register ${JSON.stringify(bankProducts)}`)
            this.purchaseStore.register(bankProducts)
            this.setupListeners(bankProductsIds)

        }).catch((error) => {
            IapService.log(`PurchaseStore: IAP error ${error}`)
        })
    }

    approvedCallback(p: IAPProduct) {
        return p.verify();
    }

    cancelledCallback(p: IAPProduct) {
        IapService.log(`PurchaseStore: cancelled ${JSON.stringify(p)}`)
    }

    errorCallback(p: IAPError) {
        IapService.log(`PurchaseStore: error: ${JSON.stringify(p)}`)
    }

    invalidCallback(p: IAPProduct) {
        IapService.log("PurchaseStore: invalid")
    }

    refundedCallback(p: IAPProduct) {
        IapService.log("PurchaseStore: refunded")
    }

    unverifiedCallback(p: IAPProduct) {
        IapService.log("PurchaseStore: unverified")
    }

    verifiedCallback(p: IAPProduct) {
        IapService.log("PurchaseStore: verified")
        p.finish()
    }

    finishedCallback(p: IAPProduct) {
        let encodedProductId = btoa(p.id)
        let encodedSignature = btoa(process.env.VUE_APP_GOOGLE_BILLING_KEY + p.id)
        let postData = encodedSignature + "\." + encodedProductId
        let urlString = `/api/integration/google/buy?product_id=${p.id}&app_type=${process.env.VUE_APP_MOBILE_APP_TYPE}`;
        http.post(urlString, postData).then(function (response) {
            IapService.log(`PurchaseStore: ${process.env.VUE_APP_API_HOST}${urlString} RESPONSE: ${JSON.stringify(response)}`)
        }).catch(function (error) {
            IapService.log(`PurchaseStore: ${process.env.VUE_APP_API_HOST}${urlString} ERROR: ${JSON.stringify(error)}`)
        });
        IapService.log("PurchaseStore: finished")
        IapService.log(`PurchaseStore: Trying to Tenjin transaction event. Data: [id: ${p.id}, currency: ${p.currency}, quantity: 1, priceMicros: ${p.priceMicros}]`)
        try {
            this.tenjinService.transaction(p.id, p.currency, 1, p.priceMicros / 1000000)
            IapService.log("PurchaseStore: Success Tenjin transaction event")
        } catch (e) {
            IapService.log(`PurchaseStore: Error Tenjin transaction event ${e.message}`)
        }
    }

    initiatedCallback(p: IAPProduct) {
        IapService.log(`PurchaseStore: initiated ${JSON.stringify(p)}`)
    }

    registeredCallback(p: IAPProduct) {
        IapService.log(`PurchaseStore: registered ${JSON.stringify(p)}`)
    }

    public setupListeners(gemsIds: string[]) {
        IapService.log(`PurchaseStore: setupListeners DEBUG ${JSON.stringify(gemsIds)}`)

        gemsIds.forEach(gemItem => {
            IapService.log(`PurchaseStore: setup listeners for ${gemItem}`)
            this.purchaseStore.when(gemItem)
                .approved((p: IAPProduct) => this.approvedCallback(p))
                .cancelled((p: IAPProduct) => this.cancelledCallback(p))
                .error((p: IAPError) => this.errorCallback(p))
                .invalid((p: IAPProduct) => this.invalidCallback(p))
                .refunded((p: IAPProduct) => this.refundedCallback(p))
                .unverified((p: IAPProduct) => this.unverifiedCallback(p))
                .verified((p: IAPProduct) => this.verifiedCallback(p))
                .finished((p: IAPProduct) => this.finishedCallback(p))
                .initiated((p: IAPProduct) => this.initiatedCallback(p))
                .registered((p: IAPProduct) => this.registeredCallback(p))
        })
        IapService.log(`PurchaseStore: refresh`)
        this.purchaseStore.refresh()
    }

    purchase(product: IAPProduct) {
        IapService.log(`PurchaseStore: order ${JSON.stringify(product)}`)
        this.purchaseStore.order(product).then((p: any) => {
            IapService.log(`PurchaseStore: order result ${JSON.stringify(p)}`)
        }, (e: any) => {
            IapService.log(`PurchaseStore: order error ${JSON.stringify(e)}`)
        })
    }

    restore() {
        IapService.log(`PurchaseStore: refresh`)
        this.purchaseStore.refresh()
    }

}
